import React from "react";
import { Save, Calendar, FileText, Trash2, Edit, Plus, Archive, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button } from "../components/ui";
import { Link } from "react-router-dom";

const SavedFilings = () => {
  // Mock data - will be replaced with real API calls
  const savedFilings = [
    {
      id: 1,
      name: "2024 BPPT - Lead County Draft",
      jurisdiction: "Lead County, NO",
      year: "2024",
      lastModified: "2024-01-15",
      progress: 75,
      status: "Draft",
      estimatedValue: "$25,752",
      dueDate: "2024-04-30",
    },
    {
      id: 2,
      name: "2024 BPPT - Harris County",
      jurisdiction: "Harris County, TX",
      year: "2024",
      lastModified: "2024-01-10",
      progress: 40,
      status: "Draft",
      estimatedValue: "$18,945",
      dueDate: "2024-05-31",
    },
    {
      id: 3,
      name: "2024 BPPT - King County Backup",
      jurisdiction: "King County, WA",
      year: "2024",
      lastModified: "2024-01-05",
      progress: 90,
      status: "Ready to File",
      estimatedValue: "$32,150",
      dueDate: "2024-04-30",
    },
    {
      id: 4,
      name: "2023 BPPT - Lead County Archive",
      jurisdiction: "Lead County, NO",
      year: "2023",
      lastModified: "2023-04-20",
      progress: 100,
      status: "Archived",
      estimatedValue: "$24,300",
      dueDate: "2023-04-30",
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "Ready to File":
        return "bg-success/10 text-success border-success/20";
      case "Draft":
        return "bg-warning/10 text-warning border-warning/20";
      case "Archived":
        return "bg-neutral-100 text-neutral-600 border-neutral-200";
      default:
        return "bg-info/10 text-info border-info/20";
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 90) return "bg-success";
    if (progress >= 60) return "bg-warning";
    return "bg-primary";
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">Saved Filings</h1>
              <p className="text-neutral-600 mt-1">
                Manage your draft and saved business property tax filings
              </p>
            </div>
            <Link to="/filing/start">
              <Button variant="cta" size="lg" className="gap-2">
                <Plus className="h-5 w-5" />
                Start New Filing
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">Total Saved</CardTitle>
              <Save className="h-4 w-4 text-neutral-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-neutral-900">{savedFilings.length}</div>
              <p className="text-xs text-neutral-500">Draft filings</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">Ready to File</CardTitle>
              <FileText className="h-4 w-4 text-neutral-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">
                {savedFilings.filter((f) => f.status === "Ready to File").length}
              </div>
              <p className="text-xs text-neutral-500">Completed drafts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">In Progress</CardTitle>
              <Clock className="h-4 w-4 text-neutral-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-warning">
                {savedFilings.filter((f) => f.status === "Draft").length}
              </div>
              <p className="text-xs text-neutral-500">Active drafts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">Archived</CardTitle>
              <Archive className="h-4 w-4 text-neutral-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-neutral-600">
                {savedFilings.filter((f) => f.status === "Archived").length}
              </div>
              <p className="text-xs text-neutral-500">Previous years</p>
            </CardContent>
          </Card>
        </div>

        {/* Filings List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Save className="h-5 w-5" />
              Your Saved Filings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {savedFilings.map((filing) => (
                <div
                  key={filing.id}
                  className="border rounded-lg p-6 hover:bg-neutral-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-neutral-900">{filing.name}</h3>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            filing.status
                          )}`}
                        >
                          {filing.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-neutral-600">
                        <div>
                          <span className="font-medium">Jurisdiction:</span>
                          <br />
                          {filing.jurisdiction}
                        </div>
                        <div>
                          <span className="font-medium">Tax Year:</span>
                          <br />
                          {filing.year}
                        </div>
                        <div>
                          <span className="font-medium">Estimated Value:</span>
                          <br />
                          {filing.estimatedValue}
                        </div>
                        <div>
                          <span className="font-medium">Due Date:</span>
                          <br />
                          {filing.dueDate}
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-neutral-700">
                            Progress: {filing.progress}%
                          </span>
                          <span className="text-xs text-neutral-500">
                            Last modified: {filing.lastModified}
                          </span>
                        </div>
                        <div className="w-full bg-neutral-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(
                              filing.progress
                            )}`}
                            style={{ width: `${filing.progress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-4 border-t border-neutral-200">
                    {filing.status !== "Archived" && (
                      <>
                        <Link to={`/filing/continue/${filing.id}`}>
                          <Button variant="primary" size="sm" className="gap-1">
                            <Edit className="h-3 w-3" />
                            Continue Filing
                          </Button>
                        </Link>

                        <Button variant="outline" size="sm" className="gap-1">
                          <Archive className="h-3 w-3" />
                          Archive
                        </Button>
                      </>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </Button>

                    {filing.status === "Ready to File" && (
                      <Button variant="cta" size="sm" className="gap-1 ml-auto">
                        <FileText className="h-3 w-3" />
                        Submit Filing
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {savedFilings.length === 0 && (
              <div className="text-center py-12">
                <Save className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No saved filings yet</h3>
                <p className="text-neutral-600 mb-6">
                  Start a new filing to see your saved drafts here
                </p>
                <Link to="/filing/start">
                  <Button variant="primary" className="gap-2">
                    <Plus className="h-4 w-4" />
                    Start Your First Filing
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SavedFilings;
